<template>
  <!-- Horizontal Navbar -->
  <HorizontalNavbar
    v-if="isHorizontal"
    :is-mobile="isMobile"
    :is-mobile-menu-open="isMobileMenuOpen"
    :is-vertical="isVertical"
    :is-horizontal="isHorizontal"
    :unread-alert-count="unreadAlertCount"
    @toggle-mobile-menu="toggleMobileMenu"
    @toggle-navbar-position="toggleNavbarPosition"
    @notification-click="handleNotificationClick"
    @logout="handleLogout"
  />

  <!-- Vertical Sidebar - Only show on desktop (horizontal screens) -->
  <VerticalSidebar
    v-else-if="isVertical && !isMobile"
    :is-mobile="isMobile"
    :is-mobile-menu-open="isMobileMenuOpen"
    :is-vertical="isVertical"
    :is-horizontal="isHorizontal"
    :unread-alert-count="unreadAlertCount"
    @toggle-mobile-menu="toggleMobileMenu"
    @toggle-navbar-position="toggleNavbarPosition"
    @notification-click="handleNotificationClick"
    @logout="handleLogout"
  />

  <!-- Mobile menu -->
  <MobileMenu
    :is-open="isMobileMenuOpen"
    @close="isMobileMenuOpen = false"
    @logout="handleLogout"
  />
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';

// Props
const props = defineProps({
  subscriptionFeatures: {
    type: Object,
    default: () => ({
      has_kitchen_module: false,
      has_ingredients_module: false,
      has_inventory_module: false,
      has_advanced_reports: false
    })
  }
});
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { usePermissions } from '@/composables/usePermissions';
import { hasRestaurantContext } from '@/utils/subdomain';
import { useI18n } from 'vue-i18n';
import { alertService } from '@/services/alertService';
import { useAppVersion } from '@/composables/useAppVersion';
import { useNavbarLayout } from '@/composables/useNavbarLayout';
import { isElectron } from '@/utils/platform';
import HorizontalNavbar from './HorizontalNavbar.vue';
import VerticalSidebar from './VerticalSidebar.vue';
import MobileMenu from './MobileMenu.vue';

const router = useRouter();


const authStore = useAuthStore();
const route = useRoute();
const { t } = useI18n();
const { appVersion } = useAppVersion();
const { canViewSubscription } = usePermissions();
const isMobileMenuOpen = ref(false);
const unreadAlertCount = ref(0);
const isMobile = ref(false);

const { isVertical, isHorizontal, toggleNavbarPosition, navbarPosition } = useNavbarLayout(isMobile);
let alertCheckInterval = null;



function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
}


function handleNotificationClick() {
  router.push('/subscription?tab=alerts');
}

async function handleLogout() {
  try {
    // Clear auth state
    await authStore.logout();
  } catch (error) {
    console.error('Logout failed:', error);
  }
  
  // Use router navigation in Electron, window.location in web
  if (isElectron()) {
    // In Electron, use Vue Router to avoid blank screen
    router.push({ name: 'Login', replace: true });
  } else {
    // In web, use window.location to preserve subdomain
    window.location.href = window.location.origin + '/login';
  }
}

// Load alert count
async function loadAlertCount() {
  if (canViewSubscription.value && hasRestaurantContext()) {
    try {
      unreadAlertCount.value = await alertService.getUnreadCount();
    } catch (error) {
      // Silently fail - alerts are not critical
      console.error('Failed to load alert count:', error);
    }
  }
}


// Check if screen is mobile
function checkMobile() {
  isMobile.value = window.innerWidth < 768; // md breakpoint
}

// Handle window resize
function handleResize() {
  const wasMobile = isMobile.value;
  checkMobile();
  
  // Si cambiamos a móvil y estábamos en modo vertical, forzar cambio a horizontal
  if (!wasMobile && isMobile.value && navbarPosition.value === 'vertical') {
    navbarPosition.value = 'horizontal';
  }
}

// Start periodic alert check
onMounted(() => {
  loadAlertCount();
  // Check every 2 minutes
  alertCheckInterval = setInterval(loadAlertCount, 120000);
  
  
  // Check mobile status and add resize listener
  checkMobile();
  
  // Si cargamos en móvil y estaba guardado el modo vertical, forzar cambio a horizontal
  if (isMobile.value && navbarPosition.value === 'vertical') {
    navbarPosition.value = 'horizontal';
  }
  
  window.addEventListener('resize', handleResize);
});

// Cleanup
onUnmounted(() => {
  if (alertCheckInterval) {
    clearInterval(alertCheckInterval);
  }
  
  
  // Remove resize listener
  window.removeEventListener('resize', handleResize);
});

// Close menus when route changes
watch(
  () => route.path,
  () => {
    isMobileMenuOpen.value = false;
  }
);
</script>
